"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { METRICS_EVENTS } from "@cede/types";
import { FeedbackBubble } from "./FeedbackBubble";
import { FeedbackForm } from "./FeedbackForm";
import { BUBBLE_SIZE, HIDDEN_STORAGE_KEY } from "./constants";
import { clampPosition, getDefaultPosition, getSavedPosition, Position, savePosition } from "./utils";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useClickOutside, useDevice, useDraggable, useEscapeKey } from "@/hooks/ui";
import { cn } from "@/lib/utils";
import { enqueueSnackbar } from "notistack";

const FORM_HEIGHT = 620;
const FORM_WIDTH = 360;
const EDGE_THRESHOLD = 120;

type Side = "top" | "right" | "bottom" | "left";

// Pick the side based on the bubble's proximity to viewport edges.
// Radix only auto-flips between opposite sides, so axis selection is up to us.
// We bias toward the chat-bubble convention (open above) for bubbles in the
// lower half, and toward horizontal placement when the bubble is near the top
// edge so the form does not have to occupy the whole vertical space.
function pickPopoverSide(position: Position): Side {
  if (typeof window === "undefined") return "top";
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const bubbleRight = vw - position.x;
  const bubbleBottom = vh - position.y;
  const bubbleLeft = bubbleRight - BUBBLE_SIZE;
  const bubbleTop = bubbleBottom - BUBBLE_SIZE;

  const space: Record<Side, number> = {
    top: bubbleTop,
    bottom: vh - bubbleBottom,
    left: bubbleLeft,
    right: vw - bubbleRight,
  };

  const nearTop = space.top < EDGE_THRESHOLD;
  const nearBottom = space.bottom < EDGE_THRESHOLD;
  const nearLeft = space.left < EDGE_THRESHOLD;
  const nearRight = space.right < EDGE_THRESHOLD;

  const horizontalCandidates: Side[] = [];
  if (!nearRight) horizontalCandidates.push("right");
  if (!nearLeft) horizontalCandidates.push("left");
  horizontalCandidates.sort((a, b) => space[b] - space[a]);

  const verticalCandidates: Side[] = [];
  if (!nearTop) verticalCandidates.push("top");
  if (!nearBottom) verticalCandidates.push("bottom");
  verticalCandidates.sort((a, b) => space[b] - space[a]);

  const candidates: Side[] = nearTop
    ? [...horizontalCandidates, ...verticalCandidates]
    : [...verticalCandidates, ...horizontalCandidates];

  for (const side of candidates) {
    const required = side === "top" || side === "bottom" ? FORM_HEIGHT : FORM_WIDTH;
    if (space[side] >= required) return side;
  }

  return (Object.entries(space) as [Side, number][]).reduce((best, cur) => (cur[1] > best[1] ? cur : best))[0];
}

export function FeedbackWidget() {
  const { isMobile } = useDevice();
  const { trackEvent } = useMetricsEvents();

  const [isOpen, setIsOpen] = useState(false);
  const [popoverSide, setPopoverSide] = useState<Side>("top");
  const [isMobileAnimating, setIsMobileAnimating] = useState(false);
  const [isHidden, setIsHidden] = useState(true);
  const [position, setPosition] = useState<Position>(() => getDefaultPosition(false));

  const mobileFormRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);

  // Initialize: check hidden state and load saved position
  useEffect(() => {
    const hidden = localStorage.getItem(HIDDEN_STORAGE_KEY);
    setIsHidden(hidden === "true");

    const saved = getSavedPosition();
    if (saved) {
      setPosition(clampPosition(saved));
    } else {
      setPosition(getDefaultPosition(isMobile));
    }
  }, [isMobile]);

  // Re-clamp position on resize
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Dragging support (touch on mobile, mouse on desktop)
  const { isDragging, handleMouseDown } = useDraggable({
    elementRef: bubbleRef,
    enabled: !isOpen,
    position,
    onPositionChange: setPosition,
    onDragEnd: savePosition,
    clampPosition,
    enableMouse: true,
  });

  const handleClose = useCallback(() => {
    if (isMobile) {
      setIsMobileAnimating(false);
      setTimeout(() => setIsOpen(false), 200);
    } else {
      setIsOpen(false);
    }
  }, [isMobile]);

  // Mobile-only: click-outside and escape handlers. Desktop is covered by Radix Popover.
  useClickOutside(mobileFormRef, handleClose, { enabled: isOpen && isMobile });
  useEscapeKey(handleClose, isOpen && isMobile);

  const handleOpen = useCallback(() => {
    if (isDragging || isOpen) return;
    if (!isMobile) setPopoverSide(pickPopoverSide(position));
    setIsOpen(true);
    trackEvent(METRICS_EVENTS.FEEDBACK_MODAL_OPENED, {});
    if (isMobile) {
      requestAnimationFrame(() => requestAnimationFrame(() => setIsMobileAnimating(true)));
    }
  }, [isDragging, isOpen, isMobile, position, trackEvent]);

  const handleSuccess = useCallback(() => {
    handleClose();
    enqueueSnackbar("Feedback sent successfully!", { variant: "success" });
  }, [handleClose]);

  if (isHidden) return null;

  const bubbleStyle = {
    right: `${position.x}px`,
    bottom: `${position.y}px`,
  };

  const bubble = (
    <div
      ref={bubbleRef}
      className={cn("fixed z-extra touch-none select-none", isDragging ? "cursor-grabbing" : "cursor-grab")}
      style={bubbleStyle}
      onMouseDown={handleMouseDown}
    >
      <FeedbackBubble onClick={handleOpen} isFormOpen={isOpen} isDragging={isDragging} />
    </div>
  );

  if (isMobile) {
    return (
      <>
        {isOpen && (
          <div
            className={cn(
              "fixed inset-0 bg-black/50 transition-opacity duration-200 z-feedback",
              isMobileAnimating ? "opacity-100" : "opacity-0",
            )}
            onClick={handleClose}
            aria-hidden="true"
          />
        )}
        {isOpen && (
          <div
            ref={mobileFormRef}
            className={cn(
              "fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
              "w-[340px] max-w-[calc(100vw-32px)]",
              "bg-cede-bg-modal backdrop-blur-xl",
              "border border-cede-border-container rounded-2xl",
              "shadow-2xl shadow-black/50",
              "p-4 z-feedback",
              "transition-all duration-200 ease-out",
              isMobileAnimating ? "opacity-100 scale-100" : "opacity-0 scale-95",
            )}
          >
            <FeedbackForm onClose={handleClose} onSuccess={handleSuccess} />
          </div>
        )}
        {bubble}
      </>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={(open) => (open ? handleOpen() : handleClose())}>
      <PopoverAnchor asChild>{bubble}</PopoverAnchor>
      <PopoverContent
        side={popoverSide}
        align={popoverSide === "top" || popoverSide === "bottom" ? "end" : "center"}
        sideOffset={12}
        collisionPadding={16}
        className="w-[340px] max-w-[var(--radix-popper-available-width)] max-h-[var(--radix-popper-available-height)] overflow-y-auto bg-cede-bg-modal backdrop-blur-xl border-cede-border-container rounded-2xl shadow-2xl shadow-black/50 p-4 z-feedback"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <FeedbackForm onClose={handleClose} onSuccess={handleSuccess} />
      </PopoverContent>
    </Popover>
  );
}
